import React from 'react';
import { Icon } from '../icons.jsx';
import {
  LizziLogo, Avatar, AvatarStack, StatusChip,
  Sidebar, UserMenu, MenuItem,
  Topbar, CommandPalette,
  ChatDock, ChatMessage, FormattedText, ChatBlock,
  ToastHost,
  ProgressBar, StatPill,
  fmtMoney, fmtPct, fmtDate, fmtDateFull,
} from '../components.jsx';
import { NewBidModal, UploadModal, NotificationsPanel, notify } from '../modals.jsx';
import { useData } from '../data/DataProvider.jsx';
import {
  createAgent, updateAgent, deleteAgent, slugifyAgent,
  listAgentKnowledge, uploadAgentKnowledge, createAgentNote, deleteKnowledgeSource,
} from '../data/repo.js';

const { useState, useEffect, useRef, useMemo, useCallback } = React;

// ===============================================
// Catálogo de asistentes (los 12 GPTs) · CRUD real
//
// La identidad y el prompt de sistema viven en la tabla `agents`.
// La base de CONOCIMIENTO (subida de PDF/Word/Excel + embeddings) llega
// en la fase F2 — aquí se muestra su estado, aún no se puede alimentar.
// ===============================================

const PHASE_META = {
  1: { label: 'Fase 1 · Análisis del pliego',        desc: 'Lectura, comprensión, primer juicio operativo y económico' },
  2: { label: 'Fase 2 · Diseño de memoria',          desc: 'Índice, referencias y aplicación metodológica' },
  3: { label: 'Fase 3 · Planificación operativa',    desc: 'Roles, cronograma, riesgos' },
  4: { label: 'Fase 4 · Documentación administrativa', desc: 'Solvencia y CVs en formato oficial' },
  0: { label: 'Sin fase asignada',                   desc: 'Asistentes fuera del workflow por fases' },
};

const AREA_OPTIONS  = ['Análisis', 'Memoria', 'Plan', 'Admin.', 'Transversal'];
const COLOR_OPTIONS = [
  ['sem-info', 'Azul'], ['sem-success', 'Verde'], ['sem-violet', 'Violeta'],
  ['sem-warning', 'Ámbar'], ['sem-danger', 'Rojo'], ['sem-neutral', 'Neutro'],
];
const ICON_OPTIONS = [
  'Compass', 'FileText', 'Cube', 'Coins', 'Layout', 'Book', 'Workflow',
  'Users', 'GanttChart', 'AlertTriangle', 'Shield', 'Type', 'Sparkles',
  'Database', 'Target', 'Cpu', 'Search', 'Calculator',
];
const MODEL_OPTIONS = [
  ['auto', 'Automático (el router elige) · recomendado'],
  ['sonnet', 'Claude Sonnet 4.6'],
  ['opus', 'Claude Opus 4.8'],
  ['gemini-flash', 'Gemini 2.5 Flash'],
  ['gemini-pro', 'Gemini 2.5 Pro'],
];
const MODEL_LABEL = Object.fromEntries(MODEL_OPTIONS);

// Secciones del pipeline de análisis (ids alineados con supabase bid-pipeline).
const SECTION_OPTIONS = [
  ['', 'Todas las secciones'],
  ['decision', 'Decisión y datos base'],
  ['economic', 'Económico'],
  ['criteria', 'Criterios y compromisos'],
  ['capacity', 'Solvencia, servicio y equipo'],
  ['risk', 'Riesgos y ANS'],
  ['docs', 'Documentación y entregables'],
  ['report', 'Informe ejecutivo'],
];
const SECTION_LABEL = Object.fromEntries(SECTION_OPTIONS);

const iconOf = (key) => Icon[key] || Icon.Sparkles;
const phaseKey = (p) => ([1, 2, 3, 4].includes(p) ? p : 0);

function AssistantsScreen({ deepLink }) {
  const { demoMode, reload } = useData();
  const data = window.MockData || {};
  const ASSISTANTS = data.ASSISTANTS || [];
  const isAdmin = (data.CURRENT_USER?.role === 'admin');

  // `editing` = objeto agente (editar) | 'new' (crear) | null (cerrado)
  const [editing, setEditing] = useState(null);

  useEffect(() => {
    function on(e) { const a = ASSISTANTS.find((x) => x.id === e.detail || x.slug === e.detail); if (a) setEditing(a); }
    window.addEventListener('lizzi:open-assistant', on);
    return () => window.removeEventListener('lizzi:open-assistant', on);
  }, [ASSISTANTS]);
  useEffect(() => {
    if (deepLink) { const a = ASSISTANTS.find((x) => x.id === deepLink || x.slug === deepLink); if (a) setEditing(a); }
  }, [deepLink]);

  // Agrupar por fase (1..4 + sin fase), respetando el orden de sort_order.
  const groups = useMemo(() => {
    const by = {};
    [...ASSISTANTS]
      .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
      .forEach((a) => { const k = phaseKey(a.phase); (by[k] = by[k] || []).push(a); });
    return by;
  }, [ASSISTANTS]);
  const orderedKeys = [1, 2, 3, 4, 0].filter((k) => groups[k]?.length);

  return (
    <div className="viewport-inner fade-in">
      <div className="page-head">
        <div>
          <div className="page-eyebrow">CATÁLOGO · {ASSISTANTS.length} {ASSISTANTS.length === 1 ? 'asistente' : 'asistentes'}</div>
          <h1 className="page-title">Asistentes</h1>
          <p className="muted" style={{ fontSize: 14, marginTop: 2, maxWidth: 620 }}>
            Los GPTs del equipo, como entidades editables. Cada uno tiene su prompt de sistema y su modelo.
            La base de conocimiento (PDF, Word, Excel, imágenes) se activa en la próxima fase.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-primary" onClick={() => setEditing('new')}>
            <Icon.Plus size={14} /> Crear asistente
          </button>
        </div>
      </div>

      {orderedKeys.length === 0 && (
        <div className="card card-pad" style={{ textAlign: 'center', padding: '48px 24px' }}>
          <div className="sem sem-info" style={{ width: 48, height: 48, borderRadius: 14, justifyContent: 'center', padding: 0, margin: '0 auto 14px' }}>
            <Icon.Sparkles size={22} />
          </div>
          <div className="font-display" style={{ fontSize: 20 }}>Aún no hay asistentes</div>
          <p className="muted" style={{ fontSize: 13.5, marginTop: 6 }}>Crea el primero para empezar a estructurar el conocimiento del equipo.</p>
          <button className="btn btn-primary" style={{ marginTop: 16 }} onClick={() => setEditing('new')}><Icon.Plus size={14} /> Crear asistente</button>
        </div>
      )}

      {orderedKeys.map((k) => {
        const meta = PHASE_META[k];
        const list = groups[k];
        return (
          <div key={k} style={{ marginBottom: 30 }}>
            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 12 }}>
              <div>
                <div className="section-title">{meta.label}</div>
                <div style={{ fontSize: 12.5, color: 'var(--text-3)', marginTop: 3 }}>{meta.desc}</div>
              </div>
              <span className="chip">{list.length} {list.length === 1 ? 'asistente' : 'asistentes'}</span>
            </div>
            <div className="assistants-grid">
              {list.map((a) => <AssistantCard key={a.id} assistant={a} onOpen={() => setEditing(a)} />)}
            </div>
          </div>
        );
      })}

      {editing && (
        <AgentEditorModal
          mode={editing === 'new' ? 'create' : 'edit'}
          agent={editing === 'new' ? null : editing}
          demoMode={demoMode}
          canDelete={isAdmin}
          reload={reload}
          onClose={() => setEditing(null)}
          onSaved={async () => { setEditing(null); if (!demoMode) await reload(); }}
        />
      )}

      <style>{`
        .assistants-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; }
        @media (max-width: 1100px) { .assistants-grid { grid-template-columns: repeat(2, 1fr); } }
        @media (max-width: 620px)  { .assistants-grid { grid-template-columns: 1fr; } }
      `}</style>
    </div>
  );
}

function AssistantCard({ assistant, onOpen }) {
  const Ico = iconOf(assistant.icon);
  const paused = assistant.enabled === false;
  return (
    <div className="card" style={{ padding: 18, cursor: 'pointer', display: 'flex', flexDirection: 'column', gap: 10, position: 'relative', overflow: 'hidden', opacity: paused ? 0.6 : 1 }}
      onClick={onOpen}
      onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--line-3)'; const ic = e.currentTarget.querySelector('.ic-bg'); if (ic) ic.style.transform = 'scale(1.06)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--line-1)'; const ic = e.currentTarget.querySelector('.ic-bg'); if (ic) ic.style.transform = 'none'; }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <div className={`sem ${assistant.color} ic-bg`} style={{ width: 44, height: 44, padding: 0, justifyContent: 'center', borderRadius: 12, transition: 'transform 240ms' }}>
          <Ico size={20} />
        </div>
        {paused && <span className="chip" style={{ fontSize: 10.5 }}>Pausado</span>}
      </div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
        <span className="font-display" style={{ fontSize: 24, lineHeight: 1 }}>{assistant.name}</span>
        <span style={{ fontSize: 11, color: 'var(--text-4)' }}>{assistant.area}</span>
      </div>
      <div style={{ fontSize: 12.5, color: 'var(--text-2)', lineHeight: 1.45 }}>{assistant.tagline}</div>
      <div style={{ fontSize: 11.5, color: 'var(--text-3)', lineHeight: 1.5, minHeight: 36 }}>{assistant.summary}</div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 'auto', paddingTop: 8 }}>
        <span style={{ fontSize: 11, color: 'var(--text-4)', display: 'flex', alignItems: 'center', gap: 6 }}>
          <Icon.Database size={11} /> {assistant.knowledgeCount > 0
            ? `${assistant.knowledgeCount} ${assistant.knowledgeCount === 1 ? 'fuente' : 'fuentes'}`
            : 'Sin conocimiento aún'}
        </span>
        <button className="btn btn-ghost" style={{ fontSize: 11.5, padding: '3px 6px' }}>Editar <Icon.ArrowUpRight size={11} /></button>
      </div>
    </div>
  );
}

// ---------- Campos de formulario (calcan el estilo de modals.jsx) ----------
function FieldLabel({ children }) {
  return <span style={{ fontSize: 11.5, color: 'var(--text-3)', letterSpacing: '0.08em', textTransform: 'uppercase', fontWeight: 500 }}>{children}</span>;
}
function TextField({ label, value, onChange, placeholder }) {
  const [focus, setFocus] = useState(false);
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <FieldLabel>{label}</FieldLabel>
      <div style={{ display: 'flex', alignItems: 'center', padding: '12px 14px', border: `1px solid ${focus ? 'var(--primary)' : 'var(--line-2)'}`, background: 'var(--bg-1)', borderRadius: 10, transition: 'border 140ms', boxShadow: focus ? '0 0 0 3px var(--primary-soft)' : 'none' }}>
        <input value={value} onChange={onChange} placeholder={placeholder} onFocus={() => setFocus(true)} onBlur={() => setFocus(false)}
          style={{ flex: 1, background: 'transparent', border: 0, fontSize: 14.5, color: 'var(--text-1)' }} />
      </div>
    </label>
  );
}
function AreaField({ label, value, onChange, placeholder, rows = 3, mono = false }) {
  const [focus, setFocus] = useState(false);
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <FieldLabel>{label}</FieldLabel>
      <textarea value={value} onChange={onChange} placeholder={placeholder} rows={rows} onFocus={() => setFocus(true)} onBlur={() => setFocus(false)}
        style={{ padding: '12px 14px', border: `1px solid ${focus ? 'var(--primary)' : 'var(--line-2)'}`, background: 'var(--bg-1)', borderRadius: 10, transition: 'border 140ms', boxShadow: focus ? '0 0 0 3px var(--primary-soft)' : 'none', fontSize: mono ? 12.5 : 14, lineHeight: 1.6, color: 'var(--text-1)', resize: 'vertical', fontFamily: mono ? 'JetBrains Mono, monospace' : 'inherit' }} />
    </label>
  );
}
function SelectField({ label, value, onChange, options }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <FieldLabel>{label}</FieldLabel>
      <select value={value} onChange={onChange} style={{ padding: '12px 14px', border: '1px solid var(--line-2)', background: 'var(--bg-1)', borderRadius: 10, fontSize: 14, color: 'var(--text-1)' }}>
        {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
    </label>
  );
}

const EMPTY_AGENT = {
  name: '', area: 'Análisis', phase: 1, tagline: '', summary: '', tone: '',
  icon: 'Sparkles', color: 'sem-info', defaultModel: 'auto', enabled: true,
  systemPrompt: '', outputTemplate: [],
};

function AgentEditorModal({ mode, agent, demoMode, canDelete, reload, onClose, onSaved }) {
  const [form, setForm] = useState(EMPTY_AGENT);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const kbTouched = useRef(false);

  // Al cerrar, si se tocó el conocimiento, recarga el dataset para refrescar
  // los contadores de las tarjetas.
  const requestClose = async () => {
    onClose();
    if (kbTouched.current && !demoMode && reload) { try { await reload(); } catch (_) { /* noop */ } }
  };

  useEffect(() => {
    if (mode === 'edit' && agent) {
      setForm({
        name: agent.name || '', area: agent.area || 'Análisis',
        phase: agent.phase ?? 1, tagline: agent.tagline || '',
        summary: agent.summary || '', tone: agent.tone || '',
        icon: agent.icon || 'Sparkles', color: agent.color || 'sem-info',
        defaultModel: agent.defaultModel || 'auto', enabled: agent.enabled !== false,
        systemPrompt: agent.systemPrompt || '',
        outputTemplate: Array.isArray(agent.outputTemplate) ? agent.outputTemplate : [],
      });
    } else {
      setForm(EMPTY_AGENT);
    }
  }, [mode, agent]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const Ico = iconOf(form.icon);

  const save = async () => {
    if (!form.name.trim()) { notify('El nombre no puede estar vacío.', 'AlertTriangle'); return; }
    if (demoMode) { notify('Cambios guardados (modo demo · no se guarda).', 'CheckCircle'); onClose(); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        name: form.name.trim(),
        phase: form.phase === '' || form.phase == null ? null : Number(form.phase),
      };
      if (mode === 'create') {
        const uid = window.MockData?.CURRENT_USER?.id;
        await createAgent({ ...payload, slug: slugifyAgent(payload.name) }, uid);
        notify(`Asistente "${payload.name}" creado.`, 'CheckCircle');
      } else {
        await updateAgent(agent.id, payload);
        notify('Asistente actualizado.', 'CheckCircle');
      }
      await onSaved();
    } catch (e) {
      const msg = /duplicate key|unique/i.test(e?.message || '') ? 'Ya existe un asistente con ese nombre.' : (e?.message || 'error');
      notify('No se pudo guardar: ' + msg, 'AlertTriangle');
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (demoMode) { notify('Eliminado (modo demo · no se guarda).', 'CheckCircle'); onClose(); return; }
    if (!window.confirm(`¿Eliminar el asistente "${agent.name}"? Esta acción no se puede deshacer.`)) return;
    setDeleting(true);
    try {
      await deleteAgent(agent.id);
      notify('Asistente eliminado.', 'CheckCircle');
      await onSaved();
    } catch (e) {
      notify('No se pudo eliminar: ' + (e?.message || 'error'), 'AlertTriangle');
    } finally {
      setDeleting(false);
    }
  };

  const addTemplateRow = () => setForm((f) => ({ ...f, outputTemplate: [...f.outputTemplate, ''] }));
  const setTemplateRow = (i, v) => setForm((f) => ({ ...f, outputTemplate: f.outputTemplate.map((x, j) => (j === i ? v : x)) }));
  const delTemplateRow = (i) => setForm((f) => ({ ...f, outputTemplate: f.outputTemplate.filter((_, j) => j !== i) }));

  return (
    <div className="modal-backdrop" onClick={requestClose}>
      <div className="modal" style={{ width: 720, maxHeight: '90vh', display: 'flex', flexDirection: 'column' }} onClick={(e) => e.stopPropagation()}>
        <div style={{ padding: '22px 26px 18px', borderBottom: '1px solid var(--line-1)', display: 'flex', gap: 16, alignItems: 'flex-start' }}>
          <div className={`sem ${form.color}`} style={{ width: 52, height: 52, padding: 0, justifyContent: 'center', borderRadius: 14 }}><Ico size={22} /></div>
          <div style={{ flex: 1 }}>
            <div className="page-eyebrow" style={{ margin: 0, marginBottom: 6 }}>{mode === 'create' ? 'NUEVO ASISTENTE' : 'EDITAR ASISTENTE'}</div>
            <h3 className="font-display-heavy" style={{ fontSize: 24, margin: 0, letterSpacing: '-0.03em' }}>{form.name || 'Asistente sin nombre'}</h3>
          </div>
          <button className="btn btn-ghost btn-icon" onClick={requestClose}><Icon.X size={15} /></button>
        </div>

        <div style={{ flex: 1, overflow: 'auto', padding: '22px 26px', display: 'flex', flexDirection: 'column', gap: 18 }}>
          <TextField label="Nombre" value={form.name} onChange={set('name')} placeholder="p. ej. Budgie" />
          <TextField label="Titular (tagline)" value={form.tagline} onChange={set('tagline')} placeholder="Qué hace en una línea" />
          <AreaField label="Resumen" value={form.summary} onChange={set('summary')} placeholder="Descripción breve del asistente" rows={2} />

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14 }}>
            <SelectField label="Área" value={form.area} onChange={set('area')} options={AREA_OPTIONS.map((a) => [a, a])} />
            <SelectField label="Fase" value={String(form.phase ?? '')} onChange={(e) => setForm((f) => ({ ...f, phase: e.target.value === '' ? null : Number(e.target.value) }))} options={[['1', 'Fase 1'], ['2', 'Fase 2'], ['3', 'Fase 3'], ['4', 'Fase 4'], ['', 'Sin fase']]} />
            <TextField label="Tono" value={form.tone} onChange={set('tone')} placeholder="p. ej. directo · ejecutivo" />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14 }}>
            <SelectField label="Icono" value={form.icon} onChange={set('icon')} options={ICON_OPTIONS.map((i) => [i, i])} />
            <SelectField label="Color" value={form.color} onChange={set('color')} options={COLOR_OPTIONS} />
            <SelectField label="Modelo por defecto" value={form.defaultModel} onChange={set('defaultModel')} options={MODEL_OPTIONS} />
          </div>

          <AreaField label="Prompt del sistema" value={form.systemPrompt} onChange={set('systemPrompt')} placeholder="Rol, reglas y jerarquía de fuentes del asistente…" rows={8} mono />

          <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
              <FieldLabel>Plantilla de salida</FieldLabel>
              <button className="btn btn-ghost" style={{ fontSize: 12 }} onClick={addTemplateRow}><Icon.Plus size={13} /> Añadir paso</button>
            </div>
            {form.outputTemplate.length === 0 && (
              <p className="muted" style={{ fontSize: 12.5, margin: '2px 0 0' }}>Sin plantilla. Añade los epígrafes fijos que debe seguir la salida.</p>
            )}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {form.outputTemplate.map((row, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span className="font-mono" style={{ fontSize: 11, color: 'var(--text-4)', width: 20 }}>{i + 1}.</span>
                  <input value={row} onChange={(e) => setTemplateRow(i, e.target.value)} placeholder="Epígrafe / sección"
                    style={{ flex: 1, padding: '9px 12px', border: '1px solid var(--line-2)', background: 'var(--bg-1)', borderRadius: 8, fontSize: 13.5, color: 'var(--text-1)' }} />
                  <button className="btn btn-ghost btn-icon" onClick={() => delTemplateRow(i)}><Icon.Trash size={13} /></button>
                </div>
              ))}
            </div>
          </div>

          {/* Estado (activo/pausado) */}
          <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
            <input type="checkbox" checked={form.enabled} onChange={(e) => setForm((f) => ({ ...f, enabled: e.target.checked }))} />
            <span style={{ fontSize: 13.5, color: 'var(--text-1)' }}>Asistente activo</span>
            <span className="muted" style={{ fontSize: 12 }}>· los pausados no aparecen disponibles en el pipeline ni el chat</span>
          </label>

          {/* Base de conocimiento */}
          {mode === 'edit'
            ? <KnowledgeManager agentId={agent.id} demoMode={demoMode} onChanged={() => { kbTouched.current = true; }} />
            : (
              <div className="card" style={{ padding: 16, background: 'var(--bg-2)', borderStyle: 'dashed' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                  <Icon.Database size={16} />
                  <span className="section-title" style={{ margin: 0 }}>Base de conocimiento</span>
                </div>
                <p className="muted" style={{ fontSize: 12.5, margin: 0, lineHeight: 1.55 }}>
                  Guarda el asistente y vuelve a abrirlo para subir documentos (PDF, Word, Excel, imágenes) o notas.
                </p>
              </div>
            )}
        </div>

        <div style={{ padding: '14px 26px', borderTop: '1px solid var(--line-1)', display: 'flex', gap: 8, alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            {mode === 'edit' && canDelete && (
              <button className="btn btn-ghost" style={{ color: 'var(--sem-danger)' }} onClick={remove} disabled={deleting || saving}>
                <Icon.Trash size={13} /> {deleting ? 'Eliminando…' : 'Eliminar'}
              </button>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-ghost" onClick={requestClose} disabled={saving}>Cancelar</button>
            <button className="btn btn-primary" onClick={save} disabled={saving}>
              <Icon.Check size={14} /> {saving ? 'Guardando…' : (mode === 'create' ? 'Crear asistente' : 'Guardar cambios')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------- Gestor de base de conocimiento ----------
const KIND_ICON = { note: 'Type', document: 'FileText' };
const KB_ACCEPT = '.pdf,.txt,.md,.png,.jpg,.jpeg,.webp,.doc,.docx,.xls,.xlsx,application/pdf,image/*';

function KnowledgeStatus({ s }) {
  if (s.status === 'ready') return <span className="sem sem-success" style={{ fontSize: 10.5 }}>{s.chunk_count || 0} fragmentos</span>;
  if (s.status === 'error') return <span className="sem sem-danger" style={{ fontSize: 10.5 }} title={s.error || ''}>Error</span>;
  return <span className="sem sem-warning" style={{ fontSize: 10.5 }}>Procesando…</span>;
}

function KnowledgeManager({ agentId, demoMode, onChanged }) {
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(!demoMode);
  const [busy, setBusy] = useState(false);
  const [noteOpen, setNoteOpen] = useState(false);
  const [note, setNote] = useState({ title: '', text: '' });
  const [appliesTo, setAppliesTo] = useState('');
  const fileRef = useRef(null);

  const refresh = useCallback(async () => {
    if (demoMode) { setLoading(false); return; }
    try { setSources(await listAgentKnowledge(agentId)); }
    catch (e) { console.warn('[lizzi] conocimiento no disponible aún:', e?.message || e); }
    finally { setLoading(false); }
  }, [agentId, demoMode]);

  useEffect(() => { refresh(); }, [refresh]);

  // Sondeo mientras haya fuentes en curso.
  useEffect(() => {
    if (demoMode) return;
    const pending = sources.some((s) => s.status === 'pending' || s.status === 'processing');
    if (!pending) return;
    const t = setInterval(refresh, 4000);
    return () => clearInterval(t);
  }, [sources, refresh, demoMode]);

  const uid = () => window.MockData?.CURRENT_USER?.id;

  const onFiles = async (files) => {
    const list = Array.from(files || []).filter((f) => f.size <= 18 * 1024 * 1024);
    if (files && files.length && !list.length) { notify('Cada archivo debe pesar 18 MB o menos.', 'AlertTriangle'); return; }
    if (demoMode) { notify('En modo demo no se sube conocimiento.', 'Info'); return; }
    setBusy(true);
    try {
      for (const f of list) await uploadAgentKnowledge(agentId, f, uid(), appliesTo || null);
      onChanged && onChanged();
      notify(`${list.length} ${list.length === 1 ? 'documento añadido' : 'documentos añadidos'}. Procesando…`, 'CheckCircle');
      await refresh();
    } catch (e) { notify('No se pudo subir: ' + (e?.message || 'error'), 'AlertTriangle'); }
    finally { setBusy(false); }
  };

  const addNote = async () => {
    if (!note.text.trim()) { notify('La nota está vacía.', 'AlertTriangle'); return; }
    if (demoMode) { notify('En modo demo no se guarda conocimiento.', 'Info'); return; }
    setBusy(true);
    try {
      await createAgentNote(agentId, { title: note.title.trim() || 'Nota', text: note.text.trim(), appliesToSection: appliesTo || null }, uid());
      onChanged && onChanged();
      setNote({ title: '', text: '' }); setNoteOpen(false);
      notify('Nota añadida. Procesando…', 'CheckCircle');
      await refresh();
    } catch (e) { notify('No se pudo añadir la nota: ' + (e?.message || 'error'), 'AlertTriangle'); }
    finally { setBusy(false); }
  };

  const del = async (s) => {
    if (demoMode) { notify('En modo demo no se elimina conocimiento.', 'Info'); return; }
    if (!window.confirm(`¿Eliminar "${s.title}" del conocimiento del asistente?`)) return;
    try {
      await deleteKnowledgeSource(s);
      onChanged && onChanged();
      await refresh();
    } catch (e) { notify('No se pudo eliminar: ' + (e?.message || 'error'), 'AlertTriangle'); }
  };

  return (
    <div className="card" style={{ padding: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Icon.Database size={16} />
          <span className="section-title" style={{ margin: 0 }}>Base de conocimiento</span>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-ghost" style={{ fontSize: 12 }} onClick={() => setNoteOpen((v) => !v)} disabled={busy}><Icon.Type size={13} /> Nota</button>
          <button className="btn btn-outline" style={{ fontSize: 12 }} onClick={() => fileRef.current?.click()} disabled={busy}><Icon.Plus size={13} /> Subir documento</button>
          <input ref={fileRef} type="file" multiple accept={KB_ACCEPT} style={{ display: 'none' }} onChange={(e) => { onFiles(e.target.files); e.target.value = ''; }} />
        </div>
      </div>
      <p className="muted" style={{ fontSize: 12, margin: '0 0 10px', lineHeight: 1.5 }}>
        PDF, imágenes y texto se procesan mejor. Se convierten a datos estructurados con Gemini y se indexan como embeddings.
      </p>
      {!demoMode && (
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
          <span className="muted" style={{ fontSize: 11.5 }}>Aplica a</span>
          <select value={appliesTo} onChange={(e) => setAppliesTo(e.target.value)}
            style={{ padding: '6px 10px', border: '1px solid var(--line-2)', background: 'var(--bg-1)', borderRadius: 8, fontSize: 12.5, color: 'var(--text-1)' }}>
            {SECTION_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <span className="muted" style={{ fontSize: 11 }}>· se aplica a lo que subas o añadas ahora</span>
        </label>
      )}

      {noteOpen && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 12, padding: 12, background: 'var(--bg-2)', borderRadius: 10 }}>
          <input value={note.title} onChange={(e) => setNote((n) => ({ ...n, title: e.target.value }))} placeholder="Título de la nota (opcional)"
            style={{ padding: '9px 12px', border: '1px solid var(--line-2)', background: 'var(--bg-1)', borderRadius: 8, fontSize: 13.5, color: 'var(--text-1)' }} />
          <textarea value={note.text} onChange={(e) => setNote((n) => ({ ...n, text: e.target.value }))} placeholder="Escribe o pega el conocimiento…" rows={4}
            style={{ padding: '9px 12px', border: '1px solid var(--line-2)', background: 'var(--bg-1)', borderRadius: 8, fontSize: 13.5, color: 'var(--text-1)', resize: 'vertical', fontFamily: 'inherit' }} />
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <button className="btn btn-ghost" style={{ fontSize: 12 }} onClick={() => { setNoteOpen(false); setNote({ title: '', text: '' }); }}>Cancelar</button>
            <button className="btn btn-primary" style={{ fontSize: 12 }} onClick={addNote} disabled={busy}><Icon.Check size={13} /> Añadir</button>
          </div>
        </div>
      )}

      {loading && <p className="muted" style={{ fontSize: 12.5, margin: 0 }}>Cargando…</p>}
      {demoMode && <p className="muted" style={{ fontSize: 12.5, margin: 0 }}>En modo demo el conocimiento no se gestiona.</p>}
      {!loading && !demoMode && sources.length === 0 && (
        <p className="muted" style={{ fontSize: 12.5, margin: 0 }}>Aún no hay conocimiento. Sube un documento o añade una nota.</p>
      )}

      {sources.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {sources.map((s) => {
            const KIco = iconOf(KIND_ICON[s.kind] || 'FileText');
            return (
              <div key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px', background: 'var(--bg-2)', borderRadius: 8 }}>
                <span style={{ color: 'var(--text-3)' }}><KIco size={15} /></span>
                <span style={{ flex: 1, fontSize: 13, color: 'var(--text-1)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.title}</span>
                {s.applies_to_section && <span className="chip" style={{ fontSize: 10 }}>{SECTION_LABEL[s.applies_to_section] || s.applies_to_section}</span>}
                <KnowledgeStatus s={s} />
                <button className="btn btn-ghost btn-icon" onClick={() => del(s)} title="Eliminar"><Icon.Trash size={13} /></button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

window.AssistantsScreen = AssistantsScreen;

export { AssistantsScreen, AssistantCard, AgentEditorModal, KnowledgeManager };
